import type React from "react";
import { cn } from "@marketing-sites/utils/cn";
import { HeaderContextProvider } from "./context";
import { baseFixtureData, baseMobileMenuData } from "./fixtures";
import type {
  DesktopBreakpoint,
  HeaderLabels,
  HeaderNavigation,
  HeaderVariant,
  LinkComponentProps,
  MobileMenuNavigation,
} from "./types";
import {
  floatingDesktopShown,
  floatingDrawerHidden,
  headerId,
} from "./constants";
import { MobileNav } from "./components/mobile-nav";
import { DesktopNav } from "./components/desktop-nav";
import { Banner } from "./components/banner";
import { MessengerBannerOffset } from "./components/messenger-banner-offset";

export type {
  HeaderNavigation,
  MobileMenuNavigation,
  MobileMenuNavItem,
  NavItemClickEventDetail,
} from "./types";

type HeaderProps = {
  className?: string;
  fixtureData?: HeaderNavigation;
  mobileMenuFixtureData?: MobileMenuNavigation;
  disableMobileMenu?: boolean;
  disableLogoLink?: boolean;
  /**
   * Layout variant. Defaults to `"default"` (current production nav). Opt into
   * `"floating"` for the redesigned Fin nav; other consumers are unaffected.
   */
  variant?: HeaderVariant;
  /**
   * Floating variant only: how wide the viewport must be before the drawer
   * gives way to the full desktop row. Defaults to `"default"` (1230px); pass
   * `"wide"` (1536px) for locales whose translated labels overflow the bar
   * past that, which would otherwise clip the right-hand side.
   */
  desktopBreakpoint?: DesktopBreakpoint;
  /**
   * The consuming app's own domain (e.g. `"fin.ai"`). Links whose absolute href
   * points at a different host get a hover "leaves the site" arrow.
   */
  homeDomain?: string;
  /**
   * The nav is rendering translated labels, which run longer than English and
   * overflow the bar: lets the banner wrap below `lg`, and moves the mobile
   * cross-site link into the drawer. English fits as-is, so it defaults to
   * `false`.
   */
  translatedLabels?: boolean;
  /**
   * Override how same-domain links render. Defaults to the monorepo `Link`
   * (wraps `next/link`); pass a custom component for routes that need a native
   * anchor (e.g. click-time href mutation by an analytics decorator).
   */
  LinkComponent?: React.ComponentType<LinkComponentProps> | "a";
  /**
   * Accessible labels for the header's own chrome. Defaults to English; a
   * localised app must pass translated values.
   */
  labels?: HeaderLabels;
  bannerLinkMetadata: { analyticsId: string };
  bannerCloseMetadata: { analyticsId: string };
  desktopLogoMetadata: { analyticsId: string };
  switchHoverCardMetadata: { analyticsId: string };
  mobileLogoMetadata: { analyticsId: string };
  mobileCrossSiteMetadata: { analyticsId: string };
};

export function Header({
  fixtureData = baseFixtureData,
  mobileMenuFixtureData = baseMobileMenuData,
  disableMobileMenu = false,
  disableLogoLink = false,
  variant = "default",
  desktopBreakpoint = "default",
  homeDomain,
  translatedLabels = false,
  LinkComponent,
  labels,
  className,
  bannerLinkMetadata,
  bannerCloseMetadata,
  desktopLogoMetadata,
  switchHoverCardMetadata,
  mobileLogoMetadata,
  mobileCrossSiteMetadata,
}: HeaderProps) {
  return (
    <HeaderContextProvider
      fixtureData={fixtureData}
      mobileMenuFixtureData={mobileMenuFixtureData}
      disableMobileMenu={disableMobileMenu}
      disableLogoLink={disableLogoLink}
      variant={variant}
      desktopBreakpoint={desktopBreakpoint}
      homeDomain={homeDomain}
      translatedLabels={translatedLabels}
      LinkComponent={LinkComponent}
      labels={labels}
    >
      <MessengerBannerOffset />
      <header
        id={headerId}
        className={cn(
          "shared-header",
          "z-nav top-(--messenger-banner-height,0px) duration-250 fixed left-0 w-full transition-[top] ease-in-out",
          variant === "floating"
            ? "bg-[var(--nav-bar-bg,var(--color-off-white))] text-[var(--nav-fg,var(--color-off-black))]"
            : "text-nav-content-primary bg-nav-background border-nav-border/30 border-b min-[1140px]:border-none",
          className,
        )}
      >
        <Banner
          linkMetadata={bannerLinkMetadata}
          closeMetadata={bannerCloseMetadata}
        />
        <div
          className={cn(
            variant === "floating"
              ? "flex min-h-[66px] flex-col justify-center px-3"
              : "px-3 py-2 min-[1140px]:p-3",
          )}
        >
          <MobileNav
            className={
              variant === "floating"
                ? floatingDrawerHidden[desktopBreakpoint]
                : "min-[1140px]:hidden"
            }
            logoMetadata={mobileLogoMetadata}
            crossSiteMetadata={mobileCrossSiteMetadata}
          />
          <DesktopNav
            className={
              variant === "floating"
                ? floatingDesktopShown[desktopBreakpoint]
                : "hidden min-[1140px]:flex"
            }
            logoMetadata={desktopLogoMetadata}
            switchHoverCardMetadata={switchHoverCardMetadata}
          />
        </div>
      </header>
    </HeaderContextProvider>
  );
}

// Export the header id selector for use in other apps
Header.IdSelector = headerId;
